import type { ExternalCharacterListItem } from '@cartwhl/common/api/getExternalCharacters';

const CHARACTERS_SESSION_CACHE_PREFIX = 'cartographer:characters:v1';
const CHARACTERS_SESSION_CACHE_TTL_MS = 30 * 60 * 1000;

type CharactersSessionCachePayload = {
  cachedAt: number;
  characters: ExternalCharacterListItem[];
};

function getCharactersSessionCacheKey(
  apiKey: string,
  organizationServiceId: string | null,
): string {
  const apiKeySuffix = apiKey.slice(-8);
  const orgToken = organizationServiceId ?? 'org-unknown';
  return `${CHARACTERS_SESSION_CACHE_PREFIX}:${orgToken}:${apiKeySuffix}`;
}

export function readCharactersSessionCache(
  apiKey: string,
  organizationServiceId: string | null,
): ExternalCharacterListItem[] | null {
  if (typeof window === 'undefined') return null;

  try {
    const storageKey = getCharactersSessionCacheKey(apiKey, organizationServiceId);
    const rawValue = window.sessionStorage.getItem(storageKey);
    if (!rawValue) return null;

    const parsed = JSON.parse(rawValue) as CharactersSessionCachePayload;
    if (
      !Array.isArray(parsed.characters) ||
      typeof parsed.cachedAt !== 'number'
    ) {
      window.sessionStorage.removeItem(storageKey);
      return null;
    }

    if (Date.now() - parsed.cachedAt > CHARACTERS_SESSION_CACHE_TTL_MS) {
      window.sessionStorage.removeItem(storageKey);
      return null;
    }

    return parsed.characters;
  } catch {
    return null;
  }
}

export function writeCharactersSessionCache(
  apiKey: string,
  organizationServiceId: string | null,
  characters: ExternalCharacterListItem[],
) {
  if (typeof window === 'undefined') return;

  try {
    const storageKey = getCharactersSessionCacheKey(apiKey, organizationServiceId);
    const payload: CharactersSessionCachePayload = {
      cachedAt: Date.now(),
      characters,
    };
    window.sessionStorage.setItem(storageKey, JSON.stringify(payload));
  } catch {
    // Ignore storage write failures (private mode/quota).
  }
}
